import currencyFormatter from 'currency-formatter'

import i18n from '@/i18n/index'
import {
  abbreviate,
  getCurrencySymbolKeyByCurrency,
  getDecimalDigitsByCurrency,
} from '@/utils/amount'
import { getLanguageFromLocale } from '@/utils/i18n'
import { getCountryDataByCountryCode } from '@/utils/index'

const _getAmountHtmlTemplate = ({
  lang,
  amountClass = 'amount',
  currencyClass = 'currency',
  currency = '',
}) => {
  const isArabic = lang === 'ar'
  const isRtl = isArabic && currency !== 'SAR'
  const firstClass = isRtl ? amountClass : currencyClass
  const secondClass = isRtl ? currencyClass : amountClass
  const firstChar = isRtl ? '%v' : '%s'
  const secondChar = isRtl ? '%s' : '%v'

  return `<span class="${firstClass}">${firstChar}</span> <span class="${secondClass}">${secondChar}</span>`
}

const generateFormat = (option = {}) => {
  if (option.html) {
    return _getAmountHtmlTemplate(option)
  }

  return option?.lang === 'ar' ? '%v %s' : '%s %v'
}

const generateDashboardAmountHtmlFormat = (option = {}) => {
  const template = _getAmountHtmlTemplate({ ...option })
  const dashboardAmountTemplate = `<span class="amount-integer-part">%vi</span>.<span class="amount-decimal-part">%vd</span>`

  return template.replace('%v', dashboardAmountTemplate)
}

/**
 * @param {Object} amountObject
 * @param {Object} options
 * @return {String}
 */
function formatAmountWithCurrency(amountObject, options = {}) {
  const { amount = 0, currency = 'SAR' } = amountObject || {}
  const currencySymbolKey = getCurrencySymbolKeyByCurrency(currency)
  const symbol = i18n.t(currencySymbolKey)
  const lang = getLanguageFromLocale(i18n.locale)

  const amountFormatted = currencyFormatter.format(amount, {
    symbol,
    precision: options?.precision ?? getDecimalDigitsByCurrency(currency),
    format: generateFormat({ ...options, lang }),
  })

  if (options?.removeZero) {
    return amountFormatted.replace(/\.0{2,3}$/, '')
  }

  return amountFormatted
}

function formatDashboardAmount(amountObject, option = {}) {
  const { amount = 0, currency = 'SAR' } = amountObject || {}
  const precision = option?.precision ?? getDecimalDigitsByCurrency(currency)
  const currencySymbolKey = getCurrencySymbolKeyByCurrency(currency)
  const symbol = i18n.t(currencySymbolKey)
  const lang = getLanguageFromLocale(i18n.locale)
  const template = generateDashboardAmountHtmlFormat({
    ...option,
    lang,
    currency,
  })
  const formattedAmount = option.abbreviate
    ? abbreviate(amount, lang === 'ar')
    : `${formatNumber(amount, 2, precision)}`
  let [integerPart = '', decimalPart = ''] = `${formattedAmount}`.split('.')

  const removeZeroDecimalPart = option?.removeZeroDecimalPart ?? true

  if (!removeZeroDecimalPart && decimalPart === '') {
    decimalPart = '00'
  }

  let amountFormatted = template
    .replace('%s', symbol)
    .replace('%vi', integerPart?.toLocaleString('en-US'))
    .replace('%vd', decimalPart)

  if (removeZeroDecimalPart && (decimalPart === '' || decimalPart === '00')) {
    amountFormatted = amountFormatted.replace('.', '')
  }

  return amountFormatted
}

function formatNumber(
  amount,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2
) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount)
}

const formatPhoneNumber = (
  countryCallingCode,
  phoneNumber,
  fallbackValue = ''
) => {
  if (!countryCallingCode || !phoneNumber) {
    return fallbackValue
  }

  return `+${countryCallingCode} ${phoneNumber}`
}

function getCurrency(currency) {
  return currency ? i18n.t(`general_${currency.toLowerCase()}`) : 'SAR'
}

function getCurrencyByCountryCode(countryCode) {
  const countryData = getCountryDataByCountryCode(countryCode)
  return getCurrency(countryData?.currency)
}

function formatCurrency(currency) {
  const currencySymbolKey = getCurrencySymbolKeyByCurrency(currency)
  const symbol = i18n.t(currencySymbolKey)

  return symbol
}

/**
 * @param {String} barcodeValue
 * @return {String}
 */
function formatBarcodeValue(barcodeValue) {
  if (!barcodeValue) {
    return ''
  }

  // If the barcode value doesn't include SAR, we don't need to replace the currency symbol
  if (!barcodeValue.includes('SAR')) {
    return barcodeValue
  }

  const formatAmount = (amount) => {
    const [amountValue] = amount.split(' ')

    return `${i18n.t('general_sar')} ${amountValue}`
  }

  // If the barcode value includes a semicolon, It has the order reference id and the amount
  if (barcodeValue.includes(';')) {
    const [orderReferenceId, amount] = barcodeValue.split(';')

    return `${orderReferenceId};${formatAmount(amount)}`
  }

  return formatAmount(barcodeValue)
}

export {
  formatNumber,
  formatAmountWithCurrency,
  formatDashboardAmount,
  formatPhoneNumber,
  getCurrency,
  formatCurrency,
  getCurrencyByCountryCode,
  formatBarcodeValue,
}
